import { OUTBOX_NO_ANSWER, isSaid } from '@/domain';
import { strings } from '@/shared/strings';

/**
 * What a person is told when something fails. The lab's request of
 * 9 October 2026: a clear explanation, and nothing technical about the system.
 *
 * It began with a phone on the sign in page reading "FetchEvent.respondWith
 * received an error: no-response" and the address of the API: the browser's own
 * words for a request that got no answer, shown as they came. Some forty places
 * showed an error's `message` the same way, whatever had raised it.
 *
 * The rule now: a screen shows the words of an error only when this
 * application wrote them for a person, and a plain sentence for everything
 * else. An error says its words are such by carrying `said: true` (see
 * `isSaid` in the domain):
 *
 * - `ApiError`, the server's answer: a refusal in the server's own sentence, a
 *   fault in ours (see `statusWords`);
 * - `NoAnswerError`, when no answer came at all;
 * - `SaidError`, raised on purpose with a sentence from `strings`.
 *
 * Anything else is a fault of the program or of the browser, and its message
 * is for whoever reads the console, where it still goes.
 */

/** An error raised on purpose, whose message is a sentence written for whoever reads the screen. */
export class SaidError extends Error {
  readonly said = true as const;

  constructor(message: string) {
    super(message);
    this.name = 'SaidError';
  }
}

/**
 * The sentence to show for a failure, whatever failed: its own words when they
 * were written for a person, and otherwise one plain sentence that says
 * nothing of how the system is built. The only way a screen words a failure.
 *
 * `otherwise` is the screen's own sentence for what it was doing, where it has
 * one: "The clock could not be stopped" says more than "That could not be
 * done".
 */
export function failureWords(error: unknown, otherwise: string = strings.errors.notDone): string {
  return isSaid(error) ? error.message : otherwise;
}

/**
 * What is said of an answer by its status alone, when the server gave no
 * sentence of its own or gave one that is not shown: a fault (5xx) is never
 * said in the server's words, because a proxy in front of the API answers a
 * 502 with a page of its own and nothing a fault says is the reader's to act
 * on.
 */
export function statusWords(status: number): string {
  if (status === 502 || status === 503 || status === 504) return strings.errors.busy;
  if (status >= 500) return strings.errors.server;
  if (status === 401) return strings.errors.sessionEnded;
  if (status === 403) return strings.errors.forbidden;
  if (status === 404) return strings.errors.notFound;
  if (status === 409) return strings.errors.conflict;
  if (status === 413) return strings.errors.tooLarge;
  if (status === 429) return strings.errors.tooMany;
  return strings.errors.notDone;
}

/** What a browser or a service worker calls a request that got no answer: never a sentence of ours. */
const NO_ANSWER_IN_BROWSER_WORDS = /fetch|load failed|network|respondwith|no-response|connection was lost/i;
/** What reads like code: an address, a class of error, a bracket, a missing value. */
const TECHNICAL = /https?:|::|[{}[\]<>]|\b[A-Za-z]*(Error|Exception)\b|undefined|\bnull\b|\bat \w+ \(/;

/**
 * Why a piece of work kept on the device has not been sent, from what was
 * written beside it. Kept as text, some of it by the service worker and some
 * of it before this rule: the mark for "no answer", a bare status, a sentence
 * of the server's, or whatever a browser called the failure at the time. Only
 * a sentence is shown as it is.
 */
export function storedFailureWords(stored: string): string {
  const text = stored.trim();
  if (text === OUTBOX_NO_ANSWER) return strings.errors.unreachable;
  if (/^\d{3}$/.test(text)) {
    const status = Number(text);
    return status >= 500 ? statusWords(status) : strings.sync.refused;
  }
  if (NO_ANSWER_IN_BROWSER_WORDS.test(text)) return strings.errors.unreachable;
  if (text === '' || TECHNICAL.test(text)) return strings.errors.notDone;
  return text;
}
